import { CreateExerciseDTO, ExerciseDTO } from '@/generated';
import { useCreateExercise, useUpdateExercise } from '@/hooks/exercise-mutation.hooks';
import { useExerciseQuery } from '@/hooks/query-hooks';
import { uploadFile } from '@/utils/api-helpers';
import { objectHasId } from '@/utils/create-mode-helper';
import { validateExercise } from '@/validation/exercise';
import { useEffect, useRef, useState } from 'react';

export type ExerciseDraft = ExerciseDTO | CreateExerciseDTO;

const createEmptyExercise = (): CreateExerciseDTO => ({
  equipment: [],
  categoryIds: [],
  name: '',
  description: '',
  muscleIntensities: [],
  config: { executionType: 'reps', value: 10 },
  mainBodyZoneId: '',
  links: [],
  progressionExerciseIds: [],
  regressionExerciseIds: [],
  prerequisites: [],
  supportExercises: [],
  publiclyVisible: true,
  isUniLateral: false,
});

export const useExerciseEditor = (exerciseId?: string) => {
  const { exercise: loadedExercise } = useExerciseQuery(exerciseId);
  const [exercise, setExercise] = useState<ExerciseDraft | undefined>(() => (exerciseId ? undefined : createEmptyExercise()));
  const initialSnapshot = useRef(exercise ? JSON.stringify(exercise) : undefined);
  const [imageFile, setImageFile] = useState<File | null>();
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  const createMutation = useCreateExercise();
  const updateMutation = useUpdateExercise();

  // The page keys this editor by route ID. A refetch must not replace its draft.
  useEffect(() => {
    if (loadedExercise && initialSnapshot.current === undefined) {
      initialSnapshot.current = JSON.stringify(loadedExercise);
      setExercise(loadedExercise);
    }
  }, [loadedExercise]);

  const updateExercise = (changes: Partial<ExerciseDraft>) => {
    setExercise((current) => (current ? { ...current, ...changes } : current));
  };

  const hasUnsavedChanges = Boolean(imageFile || additionalImageFiles.length || (exercise && JSON.stringify(exercise) !== initialSnapshot.current));

  const save = async (): Promise<boolean> => {
    if (!exercise || saving.current) return false;
    const errors = validateExercise(exercise);
    setValidationErrors(errors);
    if (errors.length) return false;
    saving.current = true;
    setIsSaving(true);
    try {
      let savedExercise = exercise;
      if (imageFile) savedExercise = { ...savedExercise, imageId: await uploadFile(imageFile) };
      if (additionalImageFiles.length) {
        const ids = await Promise.all(additionalImageFiles.map(uploadFile));
        savedExercise = { ...savedExercise, additionalImageIds: [...(savedExercise.additionalImageIds ?? []), ...ids] };
      }
      if (objectHasId(savedExercise)) await updateMutation.mutateAsync(savedExercise);
      else await createMutation.mutateAsync(savedExercise);
      initialSnapshot.current = JSON.stringify(savedExercise);
      setExercise(savedExercise);
      setImageFile(null);
      setAdditionalImageFiles([]);
      return true;
    } catch {
      setValidationErrors(['The exercise could not be saved. Please try again.']);
      return false;
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  return {
    exercise,
    updateExercise,
    imageFile,
    setImageFile,
    setAdditionalImageFiles,
    validationErrors,
    hasUnsavedChanges,
    isSaving,
    save,
    isEditMode: Boolean(exercise && objectHasId(exercise)),
  };
};
